import { Box, Button, Checkbox, Divider, FormLabel, Pagination, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { ChangeEvent, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import userApi from '~/apis/user';
import utilityApi from '~/apis/utility';
import { AddNewIcon, ExportFillIcon } from '~/assets/icons';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { cast, clearInputFilter, exportExcel, handleApiError, notify } from '~/utils/helper';
import { IUser } from '~/utils/interface/user';
import { IMasterDataItem } from '~/utils/interface/master';
import UserTable from './component/UserTable';
import styles from './User.module.scss';
import ShowUserDialog from './component/show-user-dialog/ShowUserDialog';
import ResetPasswordDialog from './component/reset-password-dialog/ResetPasswordDialog';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import EditUserDialog from './component/edit-user-dialog/EditUserDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import AssignToTechnicianDialog from './component/assign-technician-dialog/AssignToTechnicianDialog';

const cx = classNames.bind(styles);

const initialValues: IUser = {
  name: '',
  username: '',
  created_at: '',
  feedback: [],
  user_status: [],
  technician: '',
  order_by: {
    field: '',
    type: '',
  },
  page_index: 1,
  page_size: 10,
};

const PAGE_OPTIONS = [
  { id: 10, name: '10' },
  { id: 20, name: '20' },
  { id: 50, name: '50' },
  { id: 100, name: '100' },
  { id: 250, name: '250' },
  { id: 500, name: '500' },
];

const User = () => {
  const { t } = useTranslation('admin/user');

  const statusOptions = [
    { id: 0, name: t('status.new') },
    { id: 1, name: t('status.active') },
    { id: 2, name: t('status.friendly') },
    { id: 3, name: t('status.block') },
  ];

  const feedbackOptions = [
    { id: '0', name: t('feedback.no') },
    { id: '1', name: t('feedback.yes') },
  ];

  const [refreshData, setRefreshData] = useState(false);
  const [agentOptions, setAgentOptions] = useState<IMasterDataItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  const [users, setUsers] = useState<IUser[]>([]);
  const [user, setUser] = useState<IUser>();
  const [userIds, setUserIds] = useState<Array<string>>([]);

  const [openUserDialog, setOpenUserDialog] = useState<boolean>(false);
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState<boolean>(false);
  const [isOpenDeleteUserDialog, setIsOpenDeleteUserDialog] = useState<boolean>(false);

  const [openEditUserDialog, setOpenEditUserDialog] = useState<boolean>(false);
  const [editUserDialogMode, setOpenEditUserDialogMode] = useState<string>('');

  const [openAssignTechnicianDialog, setOpenAssignTechnicianDialog] = useState<boolean>(false);

  useEffect(() => {
    if (refreshData) {
      handleGetListUser();
      setRefreshData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData]);

  useEffect(() => {
    (async () => {
      try {
        const response = await utilityApi.getListAgents();
        setAgentOptions(
          response.data.map((item, index) => {
            return {
              id: item.username,
              name: item.username + ' - ' + item.name,
              data0: item.name + index,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();
  }, []);

  const form = useFormik<IUser>({
    initialValues,
    onSubmit: async () => {
      handleGetListUser();
    },
  });

  useEffect(() => {
    handleGetListUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.page_index, form.values.page_size]);

  useEffect(() => {
    form.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values]);

  const getListUser = async (overrideParams = {}) => {
    try {
      const user_status = form.values.user_status?.length
        ? form.values.user_status?.map((item) => {
            return item;
          })
        : [];

      const feedback = form.values.feedback?.length
        ? form.values.feedback?.map((item) => {
            return item;
          })
        : [];

      return (
        await userApi.getList({
          ...form.values,
          feedback: feedback,
          user_status: user_status,
          created_at: form.values.created_at
            ? dayjs(form.values.created_at, 'DD/MM/YYYY').format('YYYY-MM-DD')
            : undefined,
          ...overrideParams,
        })
      ).data;
    } catch (error) {
      handleApiError(error);
      return { total: 0, data: [] };
    }
  };

  const handleGetListUser = async () => {
    const response = await getListUser();
    setTotalItems(response.total);
    setUsers(response.data);
  };

  const getStatus = (status: number) => {
    if (status === 0) {
      return (
        <Box className='text-[#FF9500] border border-[#FF9500] rounded-[20px] h-[25px] p-[2px]'>{t('status.new')}</Box>
      );
    }

    if (status === 1) {
      return (
        <Box className='text-[#3D9149] border border-[#3D9149] rounded-[20px]  h-[25px] p-[2px]'>
          {t('status.active')}
        </Box>
      );
    }
    if (status === 2) {
      return (
        <Box className='text-[#005F73] border border-[#005F73] rounded-[20px] h-[25px] p-[2px]'>
          {t('status.friendly')}
        </Box>
      );
    }
    if (status === 3) {
      return (
        <Box className='text-[#72777B] border border-[#72777B] rounded-[20px] h-[25px] p-[2px]'>
          {t('status.block')}
        </Box>
      );
    }

    return '';
  };

  const getFeedbackName = (value: number) => {
    if (value === 0) return t('feedback.no');
    if (value === 1) return t('feedback.yes');

    return '';
  };

  const renderStatusOptionItem = (option: { id: number; name: string }, _index: number) => {
    const isChecked = !form.values.user_status?.length
      ? false
      : form.values.user_status?.map((item: any) => Number(item))?.indexOf(Number(option.id)) > -1;

    return (
      <Box key={option.id} className='select-option w-full justify-center'>
        <Checkbox checked={isChecked} className='mr-5' />
        <Box className='w-52 text-center'>{getStatus(option.id)}</Box>
      </Box>
    );
  };

  const renderFeedbackOptionItem = (option: { id: string; name: string }, _index: number) => {
    const isChecked = !form.values.feedback?.length
      ? false
      : form.values.feedback?.map((item: any) => Number(item))?.indexOf(Number(option.id)) > -1;

    return (
      <Box key={option.id} className='select-option w-full'>
        <Checkbox checked={isChecked} />
        <Box className='w-56 text-center'>{getFeedbackName(Number(option.id))}</Box>
      </Box>
    );
  };

  const handleDeleteUser = async () => {
    try {
      if (user) {
        await userApi.delete(cast(user?.username));
        setRefreshData(true);
        setIsOpenDeleteUserDialog(false);
        notify('success', t('delete.deleteSuccess'));
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleCheckAll = async (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      const res = await getListUser({ page_index: 1, page_size: totalItems });
      setUserIds(res.data?.map((facility: IUser) => cast(facility.username)));
    } else {
      setUserIds([]);
    }
  };

  const handleCheck = async (row: IUser, checked: boolean) => {
    if (checked) {
      setUserIds((prev) => [...(prev ?? []), ...[cast(row.username)]]);
    } else {
      setUserIds(userIds.filter((user) => user !== row.username));
    }
  };

  const getStatusText = (status: number) => {
    if (status === 0) return t('status.new');
    if (status === 1) return t('status.active');
    if (status === 2) return t('status.friendly');
    if (status === 3) return t('status.block');
    return '';
  };

  const getFeedback = (value: number) => {
    if (value === 0) return t('feedback.no');
    else if (value === 1) return t('feedback.yes');
    else return '';
  };

  const exportFile = async () => {
    if (!userIds.length) return notify('error', t('emptyExportFile'));
    const response = await getListUser({ page_index: 1, page_size: totalItems });

    let res = response.data;
    if (userIds.length) {
      const filterSet = new Set(userIds);
      res = response.data?.filter((item) => filterSet.has(cast(item.username)));
      if (!res.length) {
        setUserIds([]);
        res = response.data;
      }
    }

    const columns = [
      { header: t('list.code'), key: 'username', width: 15 },
      { header: t('list.fullName'), key: 'name', width: 20 },
      { header: t('list.numberFacility'), key: 'total_locations' },
      { header: t('list.numberEquipment'), key: 'total_devices' },
      { header: t('list.createdAt'), key: 'created_at', width: 15 },
      { header: t('list.feedback'), key: 'manager_phone' },
      { header: t('list.technician'), key: 'manager_unit' },
      { header: t('list.status'), key: 'status' },
    ];

    const data = res?.map((item, index) => ({
      ...item,
      no: index + 1,
      created_at: item.created_at ? dayjs(item.created_at).format('DD/MM/YYYY') : '',
      feedback: getFeedback(Number(item.feedback)),
      status: getStatusText(Number(item.user_status)),
    }));

    exportExcel(columns, data, t('fileName'));
  };

  const handleClickAddNew = () => {
    setOpenEditUserDialog(true);
    setOpenEditUserDialogMode(DIALOG_MODE_CREATE);
  };

  return (
    <Box className={cx('w-full', 'user-screen')}>
      <Box className='w-full flex'>
        <Box className={cx('box-container')}>
          <Box className={cx('column-text-field')}>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='status'>{t('status.name')}</FormLabel>
              <BaseSelect
                options={statusOptions}
                value={form.values.user_status ?? []}
                placeholder={t('select')}
                name='user_status'
                isSearchEnable
                onChange={(e) => {
                  form.setFieldValue('user_status', e.target.value);
                }}
                multiple
                selectAllOptions={(data) => {
                  form.setFieldValue('user_status', data);
                }}
                renderOptionItem={renderStatusOptionItem}
              />
            </Box>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='feedback'>{t('feedback.name')}</FormLabel>
              <BaseSelect
                options={feedbackOptions}
                value={form.values.feedback ?? []}
                placeholder={t('select')}
                name='feedback'
                isSearchEnable
                onChange={(e) => {
                  form.setFieldValue('feedback', e.target.value);
                }}
                multiple
                selectAllOptions={(data) => {
                  form.setFieldValue('feedback', data);
                }}
                renderOptionItem={renderFeedbackOptionItem}
              />
            </Box>
          </Box>

          <Box className={cx('column-text-field')}>
            <Box className='row-text-field'>
              <FormLabel htmlFor='agent'>{t('agency')}</FormLabel>
              <BaseSelect
                options={agentOptions}
                value={form.values.agent}
                placeholder={t('select')}
                name='agent'
                isSearchEnable
                onChange={form.handleChange}
                isDisplayAllOption
              />
            </Box>
          </Box>

          <Box className='flex items-center gap-[10px]'>
            <FormLabel htmlFor='registrationDate'>{t('registrationDate')}</FormLabel>
            <Box className={`${cx('column-text-field')} justify-between`}>
              <Box className={cx('row-text-field')}>
                <FormLabel htmlFor='date_from'>{t('dateFrom')}</FormLabel>
                <BaseDatePicker
                  portalId='datePicker-portalId'
                  value={form.values.date_from}
                  dateFormat='YYYY-MM-DD'
                  onChange={(date) => {
                    form.setFieldValue('date_from', date);
                  }}
                  placeholder={t('select')}
                  calendarProps={form.values.date_to ? { maxDate: new Date(form.values.date_to) } : {}}
                />
              </Box>
              <Box className={cx('row-text-field')}>
                <FormLabel htmlFor='date_to'>{t('dateTo')}</FormLabel>
                <BaseDatePicker
                  portalId='datePicker-portalId'
                  value={form.values.date_to}
                  dateFormat='YYYY-MM-DD'
                  onChange={(date) => {
                    form.setFieldValue('date_to', date);
                  }}
                  placeholder={t('select')}
                  calendarProps={form.values.date_from ? { minDate: new Date(form.values.date_from) } : {}}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Button
          className='bg-[#ED1002] rounded-[20px] text-white w-[120px] h-[35px] hover:bg-[#ED1002] hover:opacity-80 hover:text-white'
          onClick={() => {
            clearInputFilter();
            form.resetForm();
          }}
        >
          {t('clearFilter')}
        </Button>
      </Box>
      <Divider
        sx={{
          borderColor: '#005F73',
          borderBottomWidth: 1,
          width: '100%',
          my: '14px',
        }}
      ></Divider>
      <Box className={cx('table-container')}>
        <Box className='bg-[#ED1002] flex py-[2px] px-5 justify-between items-center'>
          <Typography className='text-base font-bold text-[white]'>{t('list.title')}</Typography>
          <Box className='flex'>
            <Button startIcon={<AddNewIcon />} className='text-white font-semibold' onClick={handleClickAddNew}>
              {t('addNew')}
            </Button>
            <Button startIcon={<ExportFillIcon />} className='text-white font-semibold' onClick={exportFile}>
              {t('fileExport')}
            </Button>
          </Box>
        </Box>
        <UserTable
          pageSize={form.values.page_size ?? 10}
          users={users}
          form={form}
          onEdit={(row) => {
            setUser(row);
            setOpenEditUserDialog(true);
            setOpenEditUserDialogMode(DIALOG_MODE_EDIT);
          }}
          onDetail={(row) => {
            setUser(row);
            setOpenUserDialog(true);
          }}
          onReset={(row) => {
            setUser(row);
            setOpenResetPasswordDialog(true);
          }}
          onAssign={(row) => {
            setUser(row);
            setOpenAssignTechnicianDialog(true);
          }}
          onDelete={(row) => {
            setUser(row);
            setIsOpenDeleteUserDialog(true);
          }}
          userIds={userIds}
          handleCheck={handleCheck}
          handleCheckAll={handleCheckAll}
        />
      </Box>
      <Box className={cx('flex items-center justify-end gap-10 mt-5', 'pagination-box')}>
        <Typography>
          {t('sum')}: <span className='font-bold text-[#005f73]'>{totalItems}</span> {t('user')}
        </Typography>
        <Box className='w-[150px]'>
          <BaseSelect
            options={PAGE_OPTIONS}
            value={form.values.page_size}
            onChange={(e) => {
              form.setFieldValue('page_size', e.target.value);
              form.setFieldValue('page_index', 1);
            }}
            renderSelected={(option: any) => (
              <Typography className='px-2'>
                {option.name}/{t('page').toLowerCase()}
              </Typography>
            )}
          />
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(totalItems / (form.values.page_size ?? 1)))}
          variant='outlined'
          page={form.values.page_index ?? 1}
          onChange={(e, value: number) => {
            form.setFieldValue('page_index', value);
          }}
        />
        <Box className='flex items-center gap-3'>
          <Typography>{t('page')}</Typography>
          <Box className='rounded-[20px] border border-[#ED1002] text-center py-2 px-7'>{form.values.page_index}</Box>
        </Box>
      </Box>

      <ShowUserDialog open={openUserDialog} setOpen={setOpenUserDialog} user={user} />
      <ResetPasswordDialog open={openResetPasswordDialog} setOpen={setOpenResetPasswordDialog} user={user} />
      <AssignToTechnicianDialog
        open={openAssignTechnicianDialog}
        setOpen={setOpenAssignTechnicianDialog}
        setRefreshData={setRefreshData}
        data={user}
      />
      <ConfirmDialog
        open={isOpenDeleteUserDialog}
        title={t('delete.title')}
        description={t('delete.description', {
          username: user?.username,
        })}
        onConfirm={() => handleDeleteUser()}
        onCancel={() => setIsOpenDeleteUserDialog(false)}
      />
      <EditUserDialog
        data={user}
        open={openEditUserDialog}
        mode={editUserDialogMode}
        setOpen={setOpenEditUserDialog}
        setAssign={setOpenAssignTechnicianDialog}
        setRefreshData={setRefreshData}
      />
    </Box>
  );
};

export default memo(User);
